/**
 * Atlas.json types + parsing for the sprite lab (M2). The atlas is the
 * committed contract emitted by tools/sprites/gen.py and validated by
 * tools/check-sprites.js in CI — this side just narrows unknown JSON to
 * typed structures (AGENTS §12: data over code).
 *
 * Frame paths are relative to packages/client/assets/sprites (e.g.
 * "hero/canon/idle/s/0.png"); the lab server serves that dir at /sprites/,
 * so the URL is "/sprites/" + path with the atlas version as a cache-buster.
 */

export const DIRS = ["n", "ne", "e", "se", "s", "sw", "w", "nw"] as const;
export type Dir = (typeof DIRS)[number];

export interface AnimDef {
  fps: number;
  dirs: Dir[];
}

export interface HeroSprite {
  tile?: false;
  dims: [number, number];
  anchor: [number, number];
  layers: string[];
  tints: string[];
  anims: Record<string, AnimDef>;
  /** tint -> anim -> dir -> frame paths (legacy baked composites; the part
   *  pipeline v6 leaves this empty — the hero composites from `outfit`). */
  frames: Record<string, Record<string, Record<Dir, string[]>>>;
  /** tint -> layer -> anim -> dir -> frame paths (M3: per-tint body layers) */
  layerFrames?: Record<string, Record<string, Record<string, Record<Dir, string[]>>>>;
  /** Part-pipeline outfit contract (v6): the data-driven paper-doll.
   *  Single source of truth for layer order, the swappable catalogue and
   *  every part-frame path. */
  outfit?: HeroOutfit;
}

/** The swappable hero (tools/heropipeline). Slots are equipment; "body" is
 *  a pseudo-slot the outfit never omits (always-on part files). */
export interface HeroOutfit {
  /** z-order, bottom to top. */
  layers: string[];
  /** Dirs where the held weapon composites under the torso. */
  weaponBehindDirs: Dir[];
  /** Dirs where the carried shield composites under the torso (the LEFT-hand
   *  mirror of the weapon rule). Optional: pre-shield atlases omit it, and a
   *  missing value reads as "shield always in front". */
  shieldBehindDirs?: Dir[];
  /** Order variants contribute inside a shared layer (e.g. cape panel behind
   *  the top's sleeves). */
  slotsOrder: string[];
  /** slot -> variants (the catalogue). "body" is not listed — it is fixed. */
  slots: Record<string, string[]>;
  /** Bare hero (every in-game player renders this, tinted per player). */
  defaults: Record<string, string | null>;
  /** Stable per-part raster extents in the 96px rig coordinate space. */
  partBounds?: Record<string, PartBounds>;
  /** File layer -> direction -> z position (e.g. the cape's rear panel). */
  layerDirs?: Record<string, Partial<Record<Dir, string>>>;
  /** Equipped variant -> cosmetic slots covered while worn. */
  covers?: Record<string, string[]>;
  /** tint -> slot -> variant -> layer -> anim -> dir -> frame paths. */
  frames: Record<string, Record<string, Record<string, Record<string, Record<string, Record<Dir, string[]>>>>>>;
}

export interface PartBounds {
  dims: [number, number];
  /** Raster top-left relative to the base rig's top-left; can be negative. */
  offset: [number, number];
}

/** An equipped outfit: slot -> variant (null/absent = unequipped). */
export type Outfit = Record<string, string | null>;

export interface TileSprite {
  tile: true;
  dims: [number, number];
  anchor: [number, number];
  frames: string[];
}

/** M3 equipment item: layer frames per (tint, anim, dir) — the gear frame
 * IS the layer frame; composites happen client-side. */
export interface GearDef {
  slot: string;
  kind?: string;
  dims: [number, number];
  anchor: [number, number];
  tints: string[];
  anims: Record<string, AnimDef>;
  frames: Record<string, Record<string, Record<Dir, string[]>>>;
}

export type GearSet = Record<string, GearDef>;

/** M4 dropped-item icon: one 48×48 frame per tint (RuneScape-style lying
 *  flat; the inventory icon IS the dropped item). `frames: {tint: [path]}`
 *  — canon-only items carry just "canon". */
export interface ItemDef {
  kind?: string;
  stoneKind?: "group" | "personal";
  /** Gear items reuse gear.json parts as one source of truth. */
  fromGear?: string;
  dims: [number, number];
  anchor: [number, number];
  tints: string[];
  frames: Record<string, string[]>;
}

export type ItemSet = Record<string, ItemDef>;

/** M4 NPC (procedural creature pipeline, tools/npcpipeline): a
 *  pre-composited full-body sprite — one PNG per (tint, anim, dir, frame).
 *  Unlike the hero there is no part-composited outfit; the frame IS the whole
 *  creature. The lab renders it hero-like (anims, dirs, a stage billboard). */
export interface NpcDef {
  dims: [number, number];
  anchor: [number, number];
  tints: string[];
  anims: Record<string, AnimDef>;
  /** tint -> anim -> dir -> frame paths. */
  frames: Record<string, Record<string, Record<Dir, string[]>>>;
  /** The creature hovers (the ghost's spec `float_band`): the contact
   *  shadow is drawn fainter + smaller, since it is not in ground contact. */
  floats?: boolean;
}

export type NpcSet = Record<string, NpcDef>;

/** A world prop (the substrate, tools/sprites): one committed design each —
 *  wall / pond / table (the room furniture, State.terrain) and the two door
 *  states. The anchor is where the client places the frame relative to its
 *  own drawing origin (see worldArt.ts). */
export interface PropDef {
  dims: [number, number];
  anchor: [number, number];
  frames: string[];
}

export type PropSet = Record<string, PropDef>;

export type SpriteDef = HeroSprite | TileSprite;

export interface Atlas {
  version: number;
  sprites: Record<string, SpriteDef>;
  gear?: GearSet;
  items?: ItemSet;
  npcs?: NpcSet;
  /** World props (tools/sprites substrate). Tolerant: a missing section is
   *  fine (pre-prop atlases) — worldArt draws nothing for missing props. */
  props?: PropSet;
}

function isDir(v: unknown): v is Dir {
  return typeof v === "string" && (DIRS as readonly string[]).includes(v);
}

function isDims(v: unknown): v is [number, number] {
  return Array.isArray(v) && v.length === 2 && v.every((n) => typeof n === "number");
}

function isFrames(v: unknown): v is Record<string, string[]> {
  return typeof v === "object" && v !== null && Array.isArray((v as { frames?: unknown }).frames);
}

/** tint -> slot -> variant -> layer -> anim -> dir -> string[] (tolerant). */
function isOutfitFrames(v: unknown): v is HeroOutfit["frames"] {
  if (typeof v !== "object" || v === null) return false;
  for (const ta of Object.values(v as Record<string, unknown>)) {
    if (typeof ta !== "object" || ta === null) return false;
    for (const sa of Object.values(ta as Record<string, unknown>)) {
      if (typeof sa !== "object" || sa === null) return false;
      for (const va of Object.values(sa as Record<string, unknown>)) {
        if (typeof va !== "object" || va === null) return false;
        for (const la of Object.values(va as Record<string, unknown>)) {
          if (typeof la !== "object" || la === null) return false;
          for (const aa of Object.values(la as Record<string, unknown>)) {
            if (typeof aa !== "object" || aa === null) return false;
            for (const list of Object.values(aa as Record<string, unknown>)) {
              if (!Array.isArray(list) || !list.every((p) => typeof p === "string")) return false;
            }
          }
        }
      }
    }
  }
  return true;
}

function isPartBounds(v: unknown): v is Record<string, PartBounds> {
  if (typeof v !== "object" || v === null) return false;
  return Object.values(v).every((raw: unknown) => {
    if (typeof raw !== "object" || raw === null) return false;
    const b = raw as Record<string, unknown>;
    return isDims(b.dims) && b.dims.every((n) => Number.isInteger(n) && n > 0 && n <= 512) &&
      isDims(b.offset) && b.offset.every((n) => Number.isInteger(n) && Math.abs(n) <= 512);
  });
}

function isLayerDirs(v: unknown): v is NonNullable<HeroOutfit["layerDirs"]> {
  return typeof v === "object" && v !== null && Object.values(v).every((dirs: unknown) =>
    typeof dirs === "object" && dirs !== null && Object.entries(dirs).every(([d, z]) => isDir(d) && typeof z === "string"));
}

function isCovers(v: unknown): v is NonNullable<HeroOutfit["covers"]> {
  return typeof v === "object" && v !== null && Object.values(v).every((slots: unknown) =>
    Array.isArray(slots) && slots.every((s: unknown) => typeof s === "string"));
}

export function parseAtlas(json: unknown): { ok: true; atlas: Atlas } | { ok: false; error: string } {
  if (typeof json !== "object" || json === null) return { ok: false, error: "atlas: not an object" };
  const j = json as Record<string, unknown>;
  if (typeof j.version !== "number") return { ok: false, error: "atlas: missing numeric version" };
  if (typeof j.sprites !== "object" || j.sprites === null) return { ok: false, error: "atlas: missing sprites" };

  const sprites: Record<string, SpriteDef> = {};
  for (const [name, raw] of Object.entries(j.sprites as Record<string, unknown>)) {
    if (typeof raw !== "object" || raw === null) return { ok: false, error: `atlas: sprite ${name} not an object` };
    const s = raw as Record<string, unknown>;
    if (!isDims(s.dims) || !isDims(s.anchor)) return { ok: false, error: `atlas: sprite ${name} bad dims/anchor` };
    if (s.tile === true) {
      if (!isFrames(s)) return { ok: false, error: `atlas: tile ${name} missing frames` };
      sprites[name] = {
        tile: true,
        dims: s.dims,
        anchor: s.anchor,
        frames: s.frames as string[],
      };
      continue;
    }
    // v6 part-pipeline heroes carry the z-order in `outfit.layers` and may
    // omit the legacy `layers` vocabulary; require tints always.
    if (!Array.isArray(s.tints) || (!Array.isArray(s.layers) && s.outfit === undefined)) {
      return { ok: false, error: `atlas: sprite ${name} missing layers/tints` };
    }
    if (typeof s.anims !== "object" || s.anims === null) {
      return { ok: false, error: `atlas: sprite ${name} missing anims` };
    }
    const anims: Record<string, AnimDef> = {};
    for (const [an, a] of Object.entries(s.anims as Record<string, unknown>)) {
      const ad = a as Record<string, unknown>;
      if (typeof ad.fps !== "number" || !Array.isArray(ad.dirs) || !ad.dirs.every(isDir)) {
        return { ok: false, error: `atlas: sprite ${name} anim ${an} bad` };
      }
      anims[an] = { fps: ad.fps, dirs: ad.dirs as Dir[] };
    }
    if (typeof s.frames !== "object" || s.frames === null) {
      return { ok: false, error: `atlas: sprite ${name} missing frames` };
    }
    const frames: HeroSprite["frames"] = {};
    for (const [tint, ta] of Object.entries(s.frames as Record<string, unknown>)) {
      if (typeof ta !== "object" || ta === null) continue;
      const tintFrames: Record<string, Record<Dir, string[]>> = {};
      frames[tint] = tintFrames;
      for (const [an, aa] of Object.entries(ta as Record<string, unknown>)) {
        if (typeof aa !== "object" || aa === null) continue;
        const animFrames = {} as Record<Dir, string[]>;
        tintFrames[an] = animFrames;
        for (const [d, list] of Object.entries(aa as Record<string, unknown>)) {
          if (!isDir(d) || !Array.isArray(list)) continue;
          animFrames[d] = list as string[];
        }
      }
    }
    const layerFrames: HeroSprite["layerFrames"] = {};
    if (typeof s.layerFrames === "object" && s.layerFrames !== null) {
      // tint -> layer -> anim -> dir (M3). The pre-M3 shape (layer -> anim
      // -> dir) has no tint dimension — skip it; the lab treats a missing
      // tint as "no layers" and falls back to the composite.
      for (const [tint, la] of Object.entries(s.layerFrames as Record<string, unknown>)) {
        if (typeof la !== "object" || la === null) continue;
        const first = Object.entries(la as Record<string, unknown>)[0]?.[1];
        const isTintLevel = (first as Record<string, unknown>)?.dirs === undefined;
        if (!isTintLevel) continue; // pre-M3 shape: no tint dimension
        const layers: Record<string, Record<string, Record<Dir, string[]>>> = {};
        layerFrames[tint] = layers;
        for (const [layer, ll] of Object.entries(la as Record<string, unknown>)) {
          if (typeof ll !== "object" || ll === null) continue;
          const animFrames = {} as Record<string, Record<Dir, string[]>>;
          layers[layer] = animFrames;
          for (const [an, aa] of Object.entries(ll as Record<string, unknown>)) {
            if (typeof aa !== "object" || aa === null) continue;
            const dirFrames = {} as Record<Dir, string[]>;
            animFrames[an] = dirFrames;
            for (const [d, list] of Object.entries(aa as Record<string, unknown>)) {
              if (!isDir(d) || !Array.isArray(list)) continue;
              dirFrames[d] = list as string[];
            }
          }
        }
      }
    }
    let outfit: HeroOutfit | undefined;
    if (s.outfit !== undefined) {
      if (typeof s.outfit !== "object" || s.outfit === null) return { ok: false, error: `atlas: sprite ${name} outfit not an object` };
      const o = s.outfit as Record<string, unknown>;
      const slotsOk =
        typeof o.slots === "object" && o.slots !== null &&
        Object.values(o.slots as Record<string, unknown>).every((v) => Array.isArray(v));
      if (
        !Array.isArray(o.layers) || !o.layers.every((l) => typeof l === "string") ||
        !Array.isArray(o.weaponBehindDirs) || !o.weaponBehindDirs.every(isDir) ||
        !Array.isArray(o.slotsOrder) || !o.slotsOrder.every((l) => typeof l === "string") ||
        !slotsOk ||
        typeof o.defaults !== "object" || o.defaults === null ||
        !isOutfitFrames(o.frames) ||
        (o.partBounds !== undefined && !isPartBounds(o.partBounds)) ||
        (o.layerDirs !== undefined && !isLayerDirs(o.layerDirs)) ||
        (o.covers !== undefined && !isCovers(o.covers))
      ) {
        return { ok: false, error: `atlas: sprite ${name} bad outfit` };
      }
      outfit = {
        layers: o.layers as string[],
        weaponBehindDirs: o.weaponBehindDirs as Dir[],
        shieldBehindDirs: Array.isArray(o.shieldBehindDirs) && o.shieldBehindDirs.every(isDir)
          ? (o.shieldBehindDirs as Dir[])
          : undefined,
        slotsOrder: o.slotsOrder as string[],
        slots: o.slots as Record<string, string[]>,
        defaults: o.defaults as Record<string, string | null>,
        partBounds: o.partBounds as HeroOutfit["partBounds"],
        layerDirs: o.layerDirs as HeroOutfit["layerDirs"],
        covers: o.covers as HeroOutfit["covers"],
        frames: o.frames as HeroOutfit["frames"],
      };
    }
    sprites[name] = {
      tile: false,
      dims: s.dims,
      anchor: s.anchor,
      layers: Array.isArray(s.layers) ? (s.layers as string[]) : [],
      tints: s.tints as string[],
      anims,
      frames,
      layerFrames,
      outfit,
    };
  }

  // M3: atlas.gear — item id -> {slot, kind, tints, anims, frames}.
  const gear: GearSet = {};
  if (j.gear !== undefined && j.gear !== null) {
    if (typeof j.gear !== "object") return { ok: false, error: "atlas: gear must be an object" };
    for (const [itemId, raw] of Object.entries(j.gear as Record<string, unknown>)) {
      if (typeof raw !== "object" || raw === null) continue;
      const g = raw as Record<string, unknown>;
      if (typeof g.slot !== "string" || !isDims(g.dims) || !isDims(g.anchor)) continue;
      if (typeof g.anims !== "object" || g.anims === null) continue;
      const gAnims: Record<string, AnimDef> = {};
      for (const [an, a] of Object.entries(g.anims as Record<string, unknown>)) {
        const ad = a as Record<string, unknown>;
        if (typeof ad.fps !== "number" || !Array.isArray(ad.dirs) || !ad.dirs.every(isDir)) continue;
        gAnims[an] = { fps: ad.fps, dirs: ad.dirs as Dir[] };
      }
      if (typeof g.frames !== "object" || g.frames === null) continue;
      const gFrames: GearDef["frames"] = {};
      for (const [tint, ta] of Object.entries(g.frames as Record<string, unknown>)) {
        if (typeof ta !== "object" || ta === null) continue;
        const tintFrames: Record<string, Record<Dir, string[]>> = {};
        gFrames[tint] = tintFrames;
        for (const [an, aa] of Object.entries(ta as Record<string, unknown>)) {
          if (typeof aa !== "object" || aa === null) continue;
          const animFrames = {} as Record<Dir, string[]>;
          tintFrames[an] = animFrames;
          for (const [d, list] of Object.entries(aa as Record<string, unknown>)) {
            if (!isDir(d) || !Array.isArray(list)) continue;
            animFrames[d] = list as string[];
          }
        }
      }
      gear[itemId] = {
        slot: g.slot,
        kind: typeof g.kind === "string" ? g.kind : undefined,
        dims: g.dims,
        anchor: g.anchor,
        tints: Array.isArray(g.tints) ? (g.tints as string[]) : [],
        anims: gAnims,
        frames: gFrames,
      };
    }
  }
  // M4: atlas.items — item id -> {kind, stoneKind?, fromGear?, dims, anchor,
  // tints, frames}. Tolerant: malformed entries are skipped (a missing items
  // section simply leaves the corresponding content art absent).
  const items: ItemSet = {};
  if (j.items !== undefined && j.items !== null) {
    if (typeof j.items !== "object") return { ok: false, error: "atlas: items must be an object" };
    for (const [itemId, raw] of Object.entries(j.items as Record<string, unknown>)) {
      if (typeof raw !== "object" || raw === null) continue;
      const it = raw as Record<string, unknown>;
      if (!isDims(it.dims) || !isDims(it.anchor)) continue;
      if (typeof it.frames !== "object" || it.frames === null) continue;
      const iFrames: Record<string, string[]> = {};
      for (const [tint, list] of Object.entries(it.frames as Record<string, unknown>)) {
        if (!Array.isArray(list)) continue;
        iFrames[tint] = list as string[];
      }
      if (Object.keys(iFrames).length === 0) continue;
      items[itemId] = {
        kind: typeof it.kind === "string" ? it.kind : undefined,
        stoneKind: it.stoneKind === "personal" || it.stoneKind === "group" ? it.stoneKind : undefined,
        fromGear: typeof it.fromGear === "string" ? it.fromGear : undefined,
        dims: it.dims,
        anchor: it.anchor,
        tints: Array.isArray(it.tints) ? (it.tints as string[]) : [],
        frames: iFrames,
      };
    }
  }
  // M4: atlas.npcs — npc id -> {dims, anchor, tints, anims, frames}.
  // Tolerant: malformed entries are skipped (the lab falls back to the
  // hero-only subject list).
  const npcs: NpcSet = {};
  if (j.npcs !== undefined && j.npcs !== null) {
    if (typeof j.npcs !== "object") return { ok: false, error: "atlas: npcs must be an object" };
    for (const [npcId, raw] of Object.entries(j.npcs as Record<string, unknown>)) {
      if (typeof raw !== "object" || raw === null) continue;
      const n = raw as Record<string, unknown>;
      if (!isDims(n.dims) || !isDims(n.anchor)) continue;
      if (typeof n.anims !== "object" || n.anims === null) continue;
      const nAnims: Record<string, AnimDef> = {};
      for (const [an, a] of Object.entries(n.anims as Record<string, unknown>)) {
        const ad = a as Record<string, unknown>;
        if (typeof ad.fps !== "number" || !Array.isArray(ad.dirs) || !ad.dirs.every(isDir)) continue;
        nAnims[an] = { fps: ad.fps, dirs: ad.dirs as Dir[] };
      }
      if (typeof n.frames !== "object" || n.frames === null) continue;
      const nFrames: NpcDef["frames"] = {};
      for (const [tint, ta] of Object.entries(n.frames as Record<string, unknown>)) {
        if (typeof ta !== "object" || ta === null) continue;
        const tintFrames: Record<string, Record<Dir, string[]>> = {};
        nFrames[tint] = tintFrames;
        for (const [an, aa] of Object.entries(ta as Record<string, unknown>)) {
          if (typeof aa !== "object" || aa === null) continue;
          const animFrames = {} as Record<Dir, string[]>;
          tintFrames[an] = animFrames;
          for (const [d, list] of Object.entries(aa as Record<string, unknown>)) {
            if (!isDir(d) || !Array.isArray(list)) continue;
            animFrames[d] = list as string[];
          }
        }
      }
      npcs[npcId] = {
        dims: n.dims as [number, number],
        anchor: n.anchor as [number, number],
        tints: Array.isArray(n.tints) ? (n.tints as string[]) : [],
        anims: nAnims,
        frames: nFrames,
        ...(n.floats === true ? { floats: true } : {}),
      };
    }
  }
  // World props (the substrate): name -> {dims, anchor, frames}. Tolerant:
  // malformed entries are skipped (worldArt draws nothing for a missing
  // prop — same as a missing player).
  const props: PropSet = {};
  if (j.props !== undefined && j.props !== null) {
    if (typeof j.props !== "object") return { ok: false, error: "atlas: props must be an object" };
    for (const [name, raw] of Object.entries(j.props as Record<string, unknown>)) {
      if (typeof raw !== "object" || raw === null) continue;
      const pr = raw as Record<string, unknown>;
      if (!isDims(pr.dims) || !isDims(pr.anchor)) continue;
      if (!Array.isArray(pr.frames) || pr.frames.length === 0) continue;
      props[name] = {
        dims: pr.dims as [number, number],
        anchor: pr.anchor as [number, number],
        frames: pr.frames as string[],
      };
    }
  }
  return { ok: true, atlas: { version: j.version, sprites, gear, items, npcs, props } };
}

/** URL for a frame path, cache-busted by the atlas version. */
export function frameUrl(path: string, version: number): string {
  return `/sprites/${path}?v=${version}`;
}
